import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  Badge,
  Card,
  MultiSelect,
  PropertyList,
  SearchInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SortableHead,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableStateRow,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@h2j-data-team/ui";
import { Folder, FolderX } from "lucide-react";
import type {
  AnalysisObject,
  AnalysisObjectTiming,
  AnalysisObjectTimings,
  AnalysisMeasurementBatch,
  AnalysisMeasurementContextResult,
  AnalysisStoredObjectTimings,
  AnalysisRiskFactor,
  AnalysisRiskLevel,
  QlikEvaluation,
} from "@repo/shared";
import { DensityToggle } from "@/components/density-toggle";
import { useAnalysisTableFilters, useAnalysisTableSort } from "@/lib/use-analysis-view-preferences";
import { useTableDensity } from "@/lib/use-table-density";
import { useSearchHotkey } from "@/lib/use-search-hotkey";
import {
  buildObjectTimingIndex,
  buildQlikObjectIndex,
  formatQlikSeconds,
  formatTimingLowerBound,
  formatTimingMs,
  getObjectTimingsForContext,
  summarizeObjectTimingCoverage,
  type QlikObjectCoverage,
} from "@/lib/qlik-evaluation-format";
import { AnalysisTableCard, ExpandableRow, FlagBadge, SEARCH_CLASS } from "./analysis-table";
import { usePickerMessages } from "./use-picker-messages";
import { useSheetTitles } from "./use-sheet-titles";

type FilterMode = AnalysisRiskLevel;
type ObjectsSortCol = "title" | "type" | "riskScore" | "qlikTime" | "measuredTime";

type ObjectsFilterState = {
  filter: FilterMode[];
  search: string;
  types: string[];
  sheets: string[];
  factors: string[];
};

const OBJECTS_FILTER_DEFAULTS: ObjectsFilterState = {
  filter: [],
  search: "",
  types: [],
  sheets: [],
  factors: [],
};

const RISK_LEVELS: AnalysisRiskLevel[] = ["high", "medium", "low", "none"];

/**
 * Option value for the sheet filter. `useSheetTitles` expresses two cases as
 * absences — `null` (the object sits on no sheet) and `undefined` (the row IS a
 * sheet) — so both get their own bucket.
 *
 * A real sheet is keyed by its id, never by its label: a sheet with no title of
 * its own is labelled through `usageRefs.unknownSheet`, so a label-keyed bucket
 * would change value on a language switch and leave a selected filter that the
 * dropdown no longer shows as selected — an empty table with no visible cause.
 * Keying by id also keeps two sheets that share a title as the two distinct
 * sheets they are; the option counts tell them apart.
 */
const sheetValue = (sheetId: string | undefined, title: string | null | undefined) =>
  title === undefined ? "self" : title === null ? "none" : `id:${sheetId ?? ""}`;

const RISK_VARIANT: Record<AnalysisRiskLevel, "danger" | "warning" | "info" | "secondary"> = {
  high: "danger",
  medium: "warning",
  low: "info",
  none: "secondary",
};

/** Risk verdict of an object or of one of its expressions: level plus score. */
function RiskBadge({ level, score }: { level: AnalysisRiskLevel; score: number }) {
  const { t } = useTranslation("analyses");
  if (level === "none") {
    return <Badge variant="secondary">{t("objects.noRisk")}</Badge>;
  }
  return (
    <Badge variant={RISK_VARIANT[level]} className="whitespace-nowrap tabular-nums">
      {t(`risk.level.${level}`)} · {score}
    </Badge>
  );
}

function RiskFactorBadges({ factors }: { factors: AnalysisRiskFactor[] }) {
  const { t } = useTranslation("analyses");
  if (factors.length === 0) return <span className="text-faint">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {factors.map((f) => (
        <FlagBadge
          key={f.id}
          label={t(`riskFactors.${f.id}.title`)}
          hint={t(`riskFactors.${f.id}.description`)}
        />
      ))}
    </div>
  );
}

/**
 * A cell that explains itself on hover and on keyboard focus instead of
 * pretending to be a number. Defaults to the faint styling of an absence; a real
 * reading that still needs a caveat (a sheet's batch time) passes the mono value
 * styling instead.
 */
function HintedCell({
  label,
  hint,
  className = "text-[12px] text-faint",
}: {
  label: string;
  hint: string;
  className?: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          className={`cursor-help underline decoration-dotted underline-offset-2 ${className}`}
        >
          {label}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{hint}</TooltipContent>
    </Tooltip>
  );
}

/**
 * Qlik's cold time for one object — or the honest absence of one. Qlik reports
 * the full sheet plus only the 5 slowest objects on it, so most objects have no
 * time at all; rendering 0 there would read as "instant".
 */
function QlikTimeCell({ coverage }: { coverage: QlikObjectCoverage | undefined }) {
  const { t } = useTranslation("analyses");

  if (coverage?.uncachedSeconds !== undefined) {
    const value = formatQlikSeconds(coverage.uncachedSeconds);
    // A sheet's own reading is not an object reading: say which one this is.
    return coverage.isSheet ? (
      <HintedCell
        label={value}
        hint={t("qlikEvaluation.object.sheetTimeHint")}
        className="font-mono text-[13px] text-ink tabular-nums"
      />
    ) : (
      <span className="font-mono text-[13px] text-ink tabular-nums">{value}</span>
    );
  }
  if (coverage?.notEvaluatedCode) {
    return (
      <HintedCell
        label={t("qlikEvaluation.object.notEvaluated")}
        hint={
          coverage.notEvaluatedMessage ||
          t("qlikEvaluation.object.notEvaluatedHint", { code: coverage.notEvaluatedCode })
        }
      />
    );
  }
  // In the cached top-5 but not the cold one: the two lists hold different
  // objects, so "not covered" would be false here.
  if (coverage?.cachedSeconds !== undefined) {
    return (
      <HintedCell
        label={t("qlikEvaluation.object.cachedOnly")}
        hint={t("qlikEvaluation.object.cachedOnlyHint")}
      />
    );
  }
  return (
    <HintedCell
      label={t("qlikEvaluation.object.notCovered")}
      hint={t("qlikEvaluation.object.notCoveredHint")}
    />
  );
}

/**
 * Our own reading. `cheap` is a real measurement under the 30 ms floor, so it
 * is shown as a word rather than a number racing the ones above it, and
 * `notMeasured` says why instead of showing 0.
 */
function MeasuredTimeCell({
  timing,
  contextStatus,
}: {
  timing: AnalysisObjectTiming | undefined;
  contextStatus?: AnalysisMeasurementContextResult["status"];
}) {
  const { t } = useTranslation("analyses");

  if (!timing) {
    if (contextStatus === "failed" || contextStatus === "notExecuted") {
      return (
        <HintedCell
          label={t(`objectTimings.contextStatus.${contextStatus}`)}
          hint={t("objectTimings.contextNoReadings")}
        />
      );
    }
    return <HintedCell label={t("objectTimings.absent")} hint={t("objectTimings.absentHint")} />;
  }
  if (timing.status === "notMeasured") {
    return (
      <HintedCell
        label={notMeasuredLabel(timing, t)}
        hint={
          (timing.reason !== "calcCondition" && timing.error) ||
          t(`objectTimings.reason.${timing.reason ?? "error"}`, {
            defaultValue: t("objectTimings.reason.error"),
          })
        }
      />
    );
  }
  if (timing.status === "cheap") {
    return <HintedCell label={t("objectTimings.cheap")} hint={t("objectTimings.cheapHint")} />;
  }
  if (timing.status === "timeout") {
    return <TimeoutTimeCell lowerBoundMs={timing.lowerBoundMs} />;
  }
  return (
    <span className="font-mono text-[13px] text-ink tabular-nums">
      {formatTimingMs(timing.coldMs)}
    </span>
  );
}

/** What the app itself answered is not a missing reading, so it reads differently. */
function notMeasuredLabel(timing: AnalysisObjectTiming, t: TFunction<"analyses">): string {
  if (timing.reason === "calcCondition") return t("objectTimings.blocked");
  if (timing.reason === "notCalculable") return t("objectTimings.notCalculable");
  return t("objectTimings.notMeasured");
}

function TimeoutTimeCell({ lowerBoundMs }: { lowerBoundMs: number }) {
  const { t } = useTranslation("analyses");
  return (
    <HintedCell
      label={formatTimingLowerBound(lowerBoundMs)}
      hint={t("objectTimings.timeoutHint")}
      className="font-mono text-[13px] text-ink tabular-nums"
    />
  );
}

/** Header for our own measurement run: without the context, a timing means nothing. */
function MeasurementRunHeader({
  timings,
  batch,
  selectedContext,
  onContextChange,
}: {
  timings?: AnalysisObjectTimings | null;
  batch?: AnalysisMeasurementBatch;
  selectedContext?: AnalysisMeasurementContextResult;
  onContextChange: (order: string) => void;
}) {
  const { t } = useTranslation("analyses");
  const context = selectedContext?.resolvedContext ?? timings?.context;
  const coverage = timings
    ? summarizeObjectTimingCoverage(
        timings,
        timings.measurementPhases ?? batch?.measurementPhases,
        batch?.expectedObjectCount,
      )
    : null;

  // Never the raw id: a bookmark GUID says nothing about the measured state.
  const bookmarkKey = context?.bookmarkName
    ? context?.isDefaultBookmark
      ? "bookmarkDefault"
      : "bookmark"
    : context?.isDefaultBookmark
      ? "bookmarkDefaultUnnamed"
      : "bookmarkUnnamed";

  const contextLabel =
    context?.kind === "selections"
      ? t("objectTimings.context.selections", {
          selections: context.selections
            .map((s) => `${s.field}=${s.values.join(", ")}`)
            .join(" · "),
        })
      : context?.kind === "bookmark"
        ? t(`objectTimings.context.${bookmarkKey}`, { name: context.bookmarkName })
        : context
          ? t("objectTimings.context.clean")
          : t("objectTimings.contextUnresolved");

  const requested = selectedContext?.requestedContext;
  const requestedLabel = requested?.selections
    ? t("objectTimings.context.selections", {
        selections: requested.selections
          .map((s) => `${s.field}=${s.values.join(", ")}`)
          .join(" · "),
      })
    : requested?.bookmarkName || requested?.bookmarkId
      ? t("objectTimings.context.bookmark", {
          name: requested.bookmarkName || requested.bookmarkId,
        })
      : t("objectTimings.context.opening");

  return (
    <Card className="gap-1.5 px-4 py-3">
      {batch && selectedContext && (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <Select value={String(selectedContext.order)} onValueChange={onContextChange}>
              <SelectTrigger
                className="w-full sm:w-auto sm:min-w-[280px]"
                aria-label={t("objectTimings.chooseContext")}
              >
                <SelectValue>
                  {t("objectTimings.contextOption", {
                    order: selectedContext.order,
                    name: selectedContext.name,
                    status: t(`objectTimings.contextStatus.${selectedContext.status}`),
                  })}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {batch.contexts.map((entry) => (
                  <SelectItem key={entry.order} value={String(entry.order)}>
                    {t("objectTimings.contextOption", {
                      order: entry.order,
                      name: entry.name,
                      status: t(`objectTimings.contextStatus.${entry.status}`),
                    })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Badge
              variant={
                selectedContext.status === "completed"
                  ? "success"
                  : selectedContext.status === "failed"
                    ? "danger"
                    : "warning"
              }
            >
              {t(`objectTimings.contextStatus.${selectedContext.status}`)}
            </Badge>
            {selectedContext.status !== "notExecuted" && (
              <span className="font-mono text-[11.5px] text-faint">
                {t("objectTimings.contextDuration", {
                  duration: formatTimingMs(selectedContext.durationMs),
                })}
              </span>
            )}
          </div>
          <p className="text-[12px] text-dim">
            {t("objectTimings.requestedContext", { context: requestedLabel })}
          </p>
          {(selectedContext.reason || selectedContext.error) && (
            <p className="text-[12px] text-status-warning-foreground">
              {selectedContext.error || t(`objectTimings.phaseReason.${selectedContext.reason}`)}
            </p>
          )}
          <p className="text-[12px] text-dim">
            {t("objectTimings.requestedPhases", {
              phases: batch.measurementPhases
                .map((phase) => t(`objectTimings.phase.${phase}`))
                .join(" · "),
            })}
          </p>
          {batch.partial && (
            <Badge variant="warning">
              {t("objectTimings.batchPartial", {
                reason: t(`objectTimings.phaseReason.${batch.partialReason ?? "error"}`),
              })}
            </Badge>
          )}
        </>
      )}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="text-[10.5px] font-semibold uppercase tracking-[.08em] text-faint">
          {t("objectTimings.runTitle")}
        </span>
        <span className="text-[12px] text-dim">
          {selectedContext
            ? t("objectTimings.resolvedContext", { context: contextLabel })
            : contextLabel}
        </span>
        {timings && (
          <span className="font-mono text-[11.5px] text-faint">
            {t("objectTimings.floor", {
              rtt: Math.round(timings.medianRttMs),
              threshold: Math.round(timings.thresholdMs),
            })}
          </span>
        )}
        {timings?.partial && coverage?.incompletePhases.length === 0 && (
          <Badge variant="warning" className="whitespace-nowrap">
            {t("objectTimings.partial", {
              reason: t(`objectTimings.reason.${timings.partialReason ?? "error"}`, {
                defaultValue: t("objectTimings.reason.error"),
              }),
            })}
          </Badge>
        )}
      </div>
      {coverage ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-dim">
          <span>
            {t("objectTimings.objectCoverage", {
              completed: coverage.completedObjects,
              total: coverage.totalObjects,
            })}
          </span>
          {coverage.blockedObjects > 0 && (
            <span>{t("objectTimings.blockedCoverage", { count: coverage.blockedObjects })}</span>
          )}
          {coverage.notCalculableObjects > 0 && (
            <span>
              {t("objectTimings.notCalculableCoverage", { count: coverage.notCalculableObjects })}
            </span>
          )}
          {coverage.incompletePhases.map(({ phase, reason }) => (
            <Badge key={phase} variant="warning" className="whitespace-normal">
              {t("objectTimings.phasePartial", {
                phase: t(`objectTimings.phase.${phase}`),
                reason: t(`objectTimings.phaseReason.${reason}`),
              })}
            </Badge>
          ))}
          {coverage.omittedPhases.map((phase) => (
            <Badge key={phase} variant="outline" className="whitespace-normal">
              {t("objectTimings.phaseNotRequested", { phase: t(`objectTimings.phase.${phase}`) })}
            </Badge>
          ))}
        </div>
      ) : (
        <p className="text-[12px] text-dim">{t("objectTimings.contextNoReadings")}</p>
      )}
      {batch && (
        <p className="text-[12px] leading-relaxed text-dim">
          {t("objectTimings.batchCaveat", { duration: formatTimingMs(batch.durationMs) })}
        </p>
      )}
      <p className="text-[12px] leading-relaxed text-dim">{t("objectTimings.notComparable")}</p>
    </Card>
  );
}

function QlikObjectDetail({ coverage }: { coverage: QlikObjectCoverage }) {
  const { t } = useTranslation("analyses");
  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-dim">{t("qlikEvaluation.object.detailTitle")}</p>
      {coverage.isSheet && (
        <p className="text-[12px] leading-relaxed text-dim">
          {t("qlikEvaluation.object.sheetTimeHint")}
        </p>
      )}
      <PropertyList
        columns={2}
        className="max-w-xl"
        items={[
          {
            id: "cold",
            label: t("qlikEvaluation.object.cold"),
            mono: true,
            value:
              coverage.uncachedSeconds === undefined ? (
                <span className="text-faint">
                  {t(
                    coverage.cachedSeconds === undefined
                      ? "qlikEvaluation.object.notCovered"
                      : "qlikEvaluation.object.cachedOnly",
                  )}
                </span>
              ) : (
                formatQlikSeconds(coverage.uncachedSeconds)
              ),
          },
          {
            id: "cached",
            label: t("qlikEvaluation.object.cached"),
            mono: true,
            value:
              coverage.cachedSeconds === undefined ? (
                <span className="text-faint">{t("qlikEvaluation.object.notCovered")}</span>
              ) : (
                formatQlikSeconds(coverage.cachedSeconds)
              ),
          },
          ...(coverage.cpuQuotient1 !== undefined
            ? [
                {
                  id: "singleThreaded",
                  label: t("qlikEvaluation.object.singleThreaded"),
                  mono: true,
                  value: coverage.cpuQuotient1.toFixed(2),
                },
              ]
            : []),
        ]}
      />
      <div className="flex flex-wrap gap-1">
        {coverage.noCache && <Badge variant="warning">{t("qlikEvaluation.object.noCache")}</Badge>}
        {coverage.memoryLimitStatusCode && (
          <Badge variant="danger">
            {t("qlikEvaluation.object.memoryLimit", { code: coverage.memoryLimitStatusCode })}
          </Badge>
        )}
        {coverage.timeoutStatusCode && (
          <Badge variant="danger">
            {t("qlikEvaluation.object.timeout", { code: coverage.timeoutStatusCode })}
          </Badge>
        )}
        {coverage.notEvaluatedCode && (
          <Badge variant="outline">
            {t("qlikEvaluation.object.notEvaluatedBadge", { code: coverage.notEvaluatedCode })}
          </Badge>
        )}
      </div>
    </div>
  );
}

function MeasuredObjectDetail({ timing }: { timing: AnalysisObjectTiming }) {
  const { t } = useTranslation("analyses");
  const measures = timing.measures ?? [];

  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-dim">{t("objectTimings.detailTitle")}</p>
      <PropertyList
        columns={2}
        className="max-w-xl"
        items={[
          {
            id: "cold",
            label: t("objectTimings.cold"),
            mono: true,
            value:
              timing.status === "notMeasured" ? (
                <span className="text-faint">{notMeasuredLabel(timing, t)}</span>
              ) : timing.status === "cheap" ? (
                <span className="text-dim">{t("objectTimings.cheap")}</span>
              ) : timing.status === "timeout" ? (
                <TimeoutTimeCell lowerBoundMs={timing.lowerBoundMs} />
              ) : (
                formatTimingMs(timing.coldMs)
              ),
          },
          ...(typeof timing.unconditionedMs === "number" ||
          typeof timing.unconditionedLowerBoundMs === "number"
            ? [
                {
                  id: "unconditioned",
                  label: t("objectTimings.unconditioned"),
                  mono: true,
                  value: (
                    <HintedCell
                      label={
                        typeof timing.unconditionedMs === "number"
                          ? formatTimingMs(timing.unconditionedMs)
                          : formatTimingLowerBound(timing.unconditionedLowerBoundMs ?? 0)
                      }
                      hint={t("objectTimings.unconditionedHint")}
                      className="font-mono text-[13px] text-ink tabular-nums"
                    />
                  ),
                },
              ]
            : []),
          ...(typeof timing.cachedMs === "number"
            ? [
                {
                  id: "cachedMs",
                  label: t("objectTimings.cachedPass"),
                  mono: true,
                  value: formatTimingMs(timing.cachedMs),
                },
              ]
            : []),
          ...(typeof timing.cachedLowerBoundMs === "number"
            ? [
                {
                  id: "cachedLowerBoundMs",
                  label: t("objectTimings.cachedPass"),
                  mono: true,
                  value: <TimeoutTimeCell lowerBoundMs={timing.cachedLowerBoundMs} />,
                },
              ]
            : []),
          {
            id: "kind",
            label: t("objectTimings.kind"),
            mono: true,
            value: t(`objectTimings.defKind.${timing.kind}`),
          },
          { id: "order", label: t("objectTimings.order"), mono: true, value: `#${timing.order}` },
        ]}
      />
      {timing.reusedFromObjectId && (
        <p className="text-[12px] leading-relaxed text-dim">
          {t("objectTimings.reusedHint", { objectId: timing.reusedFromObjectId })}
        </p>
      )}
      {timing.blockedByCondition && (
        <p className="text-[12px] leading-relaxed text-status-warning-foreground">
          {/* Runs stored before the cube kept its condition carry a reading here. */}
          {t(
            timing.status === "notMeasured"
              ? "objectTimings.blockedByCondition"
              : "objectTimings.blockedByConditionMeasured",
          )}
        </p>
      )}
      {/* Absent on a run stored before the flag existed: those always probed,
          so only an explicit false means this object was never checked. */}
      {!timing.blockedByCondition &&
        timing.hadCalcCondition &&
        timing.conditionProbed === false && (
          <p className="text-[12px] leading-relaxed text-dim">
            {t("objectTimings.conditionNotChecked")}
          </p>
        )}
      {timing.status === "notMeasured" && timing.reason !== "calcCondition" && (
        <p className="text-[12px] text-dim">
          {timing.error ||
            t(`objectTimings.reason.${timing.reason ?? "error"}`, {
              defaultValue: t("objectTimings.reason.error"),
            })}
        </p>
      )}
      {measures.length === 0 &&
        timing.deconstructionSkipped &&
        timing.deconstructionSkipped !== "calcCondition" && (
          <p className="text-[12px] text-dim">
            {t(`objectTimings.reason.${timing.deconstructionSkipped}`)}
          </p>
        )}
      {measures.length > 0 && (
        <div className="space-y-1">
          <p className="text-[11.5px] text-faint">{t("objectTimings.measures")}</p>
          {measures.map((m) => (
            <div key={m.index} className="flex items-start gap-3">
              <span className="w-[70px] shrink-0 text-right font-mono text-[12px] text-ink tabular-nums">
                {m.status === "notMeasured" ? (
                  <span className="text-faint">{t("objectTimings.notMeasured")}</span>
                ) : m.status === "cheap" ? (
                  <span className="text-dim">{t("objectTimings.cheap")}</span>
                ) : m.status === "timeout" ? (
                  <TimeoutTimeCell lowerBoundMs={m.lowerBoundMs} />
                ) : (
                  formatTimingMs(m.coldMs)
                )}
              </span>
              <code className="min-w-0 flex-1 whitespace-pre-wrap wrap-break-word font-mono text-xs text-dim">
                {m.expression || m.label || `#${m.index}`}
              </code>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface ObjectsAnalysisTableProps {
  analysisId: string;
  objects: AnalysisObject[];
  /** Qlik's own evaluation, joined on objectspec.id ↔ AnalysisObject.id. */
  qlikEvaluation?: QlikEvaluation | null;
  /** Our own session-cube measurement run. */
  objectTimings?: AnalysisStoredObjectTimings | null;
}

export function ObjectsAnalysisTable({
  analysisId,
  objects,
  qlikEvaluation,
  objectTimings,
}: ObjectsAnalysisTableProps) {
  const { t } = useTranslation("analyses");
  const { t: tc } = useTranslation("common");
  const pickerMessages = usePickerMessages();
  const { density, setDensity } = useTableDensity();
  const { filters, setFilters } = useAnalysisTableFilters<ObjectsFilterState>(
    analysisId,
    "objects",
    OBJECTS_FILTER_DEFAULTS,
  );
  const filter = useMemo(
    () => (Array.isArray(filters.filter) ? filters.filter : []),
    [filters.filter],
  );
  const { search, types, sheets, factors } = filters;
  // Risk descending by default, so the objects worth looking at sit on top and
  // the (usually numerous) risk-free ones sink to the bottom.
  const { sortBy, sortDir, setSort } = useAnalysisTableSort<ObjectsSortCol>("objects", {
    sortBy: "riskScore",
    sortDir: "desc",
  });
  const [expandedObj, setExpandedObj] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  useSearchHotkey(searchRef);
  const [contextOrder, setContextOrder] = useState<string | null>(null);
  const batch = objectTimings && "contexts" in objectTimings ? objectTimings : undefined;
  const selectedContext =
    batch?.contexts.find((entry) => String(entry.order) === contextOrder) ?? batch?.contexts[0];
  const selectedTimings = getObjectTimingsForContext(objectTimings, selectedContext?.order ?? 1);
  const sheetTitles = useSheetTitles(objects);

  // The name the row actually shows. 37% of a real app's objects carry no
  // author-typed title, so display, search and sort all read from here — the id
  // is never a name.
  const nameOf = (o: AnalysisObject) => o.title || t("objects.untitled", { type: o.type });

  // Two more numeric families land in this table, and both are optional per
  // analysis. Each contributes ONE scan column; everything else about them
  // lives in the row expansion, so the table stays readable at six columns for
  // the common case and eight at most.
  const showQlik = qlikEvaluation?.status === "completed" && !!qlikEvaluation.result?.result;
  const showMeasured = !!objectTimings;
  const extraCols = (showQlik ? 1 : 0) + (showMeasured ? 1 : 0);

  const qlikIndex = useMemo(() => buildQlikObjectIndex(qlikEvaluation?.result), [qlikEvaluation]);
  const timingIndex = useMemo(() => buildObjectTimingIndex(selectedTimings), [selectedTimings]);

  // The sort column is remembered across analyses, so a column that this
  // analysis does not have falls back to risk instead of sorting by nothing.
  const effectiveSortBy: ObjectsSortCol =
    (sortBy === "qlikTime" && !showQlik) || (sortBy === "measuredTime" && !showMeasured)
      ? "riskScore"
      : sortBy;

  // Sort accessors. `undefined` means "no reading", never a number: `coldMs` is
  // signed (RTT is subtracted, and the spike saw −2 to −5 ms), so any numeric
  // sentinel would out-rank real readings. The comparator sinks them instead.
  const sortValue = (o: AnalysisObject, col: ObjectsSortCol): string | number | undefined => {
    if (col === "title") return nameOf(o);
    if (col === "type") return o.type;
    if (col === "riskScore") return o.riskScore;
    if (col === "qlikTime") return qlikIndex.get(o.id)?.uncachedSeconds;
    const timing = timingIndex.get(o.id);
    if (!timing || timing.status === "notMeasured") return undefined;
    return timing.status === "timeout" ? timing.lowerBoundMs : timing.coldMs;
  };

  const filtered = useMemo(() => {
    let result = objects;
    if (filter.length > 0) {
      result = result.filter((o) => filter.includes(o.riskLevel));
    }
    if (types.length > 0) {
      result = result.filter((o) => types.includes(o.type));
    }
    if (sheets.length > 0) {
      result = result.filter((o) => sheets.includes(sheetValue(o.sheetId, sheetTitles.get(o.id))));
    }
    if (factors.length > 0) {
      result = result.filter((o) => o.riskFactors.some((f) => factors.includes(f.id)));
    }
    if (search) {
      const lower = search.toLowerCase();
      result = result.filter(
        (o) =>
          nameOf(o).toLowerCase().includes(lower) ||
          o.id.toLowerCase().includes(lower) ||
          o.type.toLowerCase().includes(lower) ||
          o.expressions.some((e) => e.expression.toLowerCase().includes(lower)),
      );
    }
    return [...result].sort((a, b) => {
      const aVal = sortValue(a, effectiveSortBy);
      const bVal = sortValue(b, effectiveSortBy);
      if (typeof aVal === "string" && typeof bVal === "string") {
        return sortDir === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      // A row with no reading sorts last in BOTH directions — an absence is not
      // a small number, so ascending must not float ~200 of them to the top.
      if (aVal === undefined || bVal === undefined) {
        if (aVal === bVal) return nameOf(a).localeCompare(nameOf(b));
        return aVal === undefined ? 1 : -1;
      }
      // Equal risk keeps a stable, readable order: alphabetical by title.
      const diff =
        sortDir === "asc"
          ? (aVal as number) - (bVal as number)
          : (bVal as number) - (aVal as number);
      return diff !== 0 ? diff : nameOf(a).localeCompare(nameOf(b));
    });
    // `t` is read through nameOf: a language switch renames the untitled rows,
    // so search and name sort have to be recomputed with it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    objects,
    filter,
    types,
    sheets,
    factors,
    sheetTitles,
    search,
    effectiveSortBy,
    sortDir,
    qlikIndex,
    timingIndex,
    t,
  ]);

  // Compare against the EFFECTIVE column: with a persisted sort on a column
  // this analysis does not have, `sortBy` still names the hidden one, and the
  // first click on the column the indicator points at would be a no-op.
  const handleSort = (col: ObjectsSortCol) => {
    if (effectiveSortBy === col) setSort(col, sortDir === "asc" ? "desc" : "asc");
    else setSort(col, col === "title" || col === "type" ? "asc" : "desc");
  };

  const direction = (col: ObjectsSortCol) => (effectiveSortBy === col ? sortDir : undefined);

  const filterOptions = RISK_LEVELS.map((level) => ({
    level,
    count: objects.filter((o) => o.riskLevel === level).length,
  }))
    .filter(({ count }) => count > 0)
    .map(({ level, count }) => ({
      value: level,
      label: t(`objects.filter${level[0].toUpperCase()}${level.slice(1)}`, { count }),
    }));
  const selectedFilters = filterOptions.filter((o) => filter.includes(o.value)).map((o) => o.value);

  // The three data-derived filters: options are whatever this analysis actually
  // contains, so a count of 0 cannot occur. One pass replaces the risk filter's
  // per-option scan, which would be O(objects × types) with an open enum.
  // Biggest bucket first, ties alphabetical.
  const countOptions = (entries: { value: string; label: string }[]) => {
    const counts = new Map<string, { label: string; count: number }>();
    for (const { value, label } of entries) {
      const hit = counts.get(value);
      if (hit) hit.count += 1;
      else counts.set(value, { label, count: 1 });
    }
    return [...counts]
      .sort(([, a], [, b]) => b.count - a.count || a.label.localeCompare(b.label))
      .map(([value, { label, count }]) => ({
        value,
        label: t("objects.optionWithCount", { label, count }),
      }));
  };

  const typeOptions = countOptions(objects.map((o) => ({ value: o.type, label: o.type })));
  const selectedTypes = typeOptions.filter((o) => types.includes(o.value)).map((o) => o.value);

  const sheetOptions = countOptions(
    objects.map((o) => {
      const title = sheetTitles.get(o.id);
      return {
        value: sheetValue(o.sheetId, title),
        label:
          title === undefined ? t("objects.filterSheetSelf") : (title ?? t("usageRefs.noSheet")),
      };
    }),
  );
  const selectedSheets = sheetOptions.filter((o) => sheets.includes(o.value)).map((o) => o.value);

  const factorOptions = countOptions(
    objects.flatMap((o) =>
      o.riskFactors.map((f) => ({ value: f.id, label: t(`riskFactors.${f.id}.title`) })),
    ),
  );
  const selectedFactors = factorOptions
    .filter((o) => factors.includes(o.value))
    .map((o) => o.value);

  const colSpan = 6 + extraCols;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      {objectTimings && (
        <MeasurementRunHeader
          timings={selectedTimings}
          batch={batch}
          selectedContext={selectedContext}
          onContextChange={setContextOrder}
        />
      )}

      {/* Whenever the Qlik column is on screen — which is the default
          configuration, with no measurement run and therefore no header above
          to carry the caveat. */}
      {showQlik && (
        <p className="text-[12px] leading-relaxed text-dim">{t("objects.qlikTimeCaveat")}</p>
      )}

      <AnalysisTableCard
        toolbar={
          <>
            <SearchInput
              ref={searchRef}
              containerClassName={SEARCH_CLASS}
              aria-label={t("objects.searchPlaceholder")}
              placeholder={t("objects.searchPlaceholder")}
              clearLabel={tc("list.clearSearch")}
              shortcutHint="/"
              value={search}
              onValueChange={(value) => setFilters({ search: value })}
            />
            <MultiSelect
              size="compact"
              className="w-48"
              aria-label={t("objects.filterByRisk")}
              options={filterOptions}
              value={selectedFilters}
              onValueChange={(next) => setFilters({ filter: next })}
              messages={pickerMessages(t("objects.filterByRisk"))}
              maxDisplay={1}
            />
            <MultiSelect
              size="compact"
              className="w-48"
              aria-label={t("objects.filterByType")}
              options={typeOptions}
              value={selectedTypes}
              onValueChange={(next) => setFilters({ types: next })}
              messages={pickerMessages(t("objects.filterByType"))}
              maxDisplay={1}
            />
            <MultiSelect
              size="compact"
              className="w-48"
              aria-label={t("objects.filterBySheet")}
              options={sheetOptions}
              value={selectedSheets}
              onValueChange={(next) => setFilters({ sheets: next })}
              messages={pickerMessages(t("objects.filterBySheet"))}
              maxDisplay={1}
            />
            <MultiSelect
              size="compact"
              className="w-48"
              aria-label={t("objects.filterByFactor")}
              options={factorOptions}
              value={selectedFactors}
              onValueChange={(next) => setFilters({ factors: next })}
              messages={pickerMessages(t("objects.filterByFactor"))}
              maxDisplay={1}
            />
            <DensityToggle value={density} onChange={setDensity} />
          </>
        }
        footer={
          objects.length > 0 &&
          t("objects.showing", { filtered: filtered.length, total: objects.length })
        }
      >
        <Table
          className="min-w-[1000px]"
          scrollLabel={t("detail.tabs.objects")}
          scrollClassName="min-h-0 flex-1"
        >
          <TableHeader>
            <TableRow>
              <TableHead className="w-10 px-2">
                <span className="sr-only">{t("table.details")}</span>
              </TableHead>
              <SortableHead direction={direction("title")} onSort={() => handleSort("title")}>
                {t("objects.colTitle")}
              </SortableHead>
              <SortableHead
                className="w-[190px]"
                direction={direction("type")}
                onSort={() => handleSort("type")}
              >
                {t("objects.colType")}
              </SortableHead>
              <TableHead className="w-[180px]">{t("objects.colSheet")}</TableHead>
              <TableHead>{t("objects.colRiskFactors")}</TableHead>
              <SortableHead
                className="w-[150px]"
                align="end"
                direction={direction("riskScore")}
                onSort={() => handleSort("riskScore")}
              >
                {t("objects.colRisk")}
              </SortableHead>
              {showQlik && (
                <SortableHead
                  className="w-[130px]"
                  align="end"
                  direction={direction("qlikTime")}
                  onSort={() => handleSort("qlikTime")}
                >
                  {t("objects.colQlikTime")}
                </SortableHead>
              )}
              {showMeasured && (
                <SortableHead
                  className="w-[130px]"
                  align="end"
                  direction={direction("measuredTime")}
                  onSort={() => handleSort("measuredTime")}
                >
                  {t("objects.colMeasuredTime")}
                </SortableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableStateRow colSpan={colSpan} state="no-results" title={t("objects.noMatch")} />
            )}
            {filtered.map((o) => {
              const sheetTitle = sheetTitles.get(o.id);
              const coverage = showQlik ? qlikIndex.get(o.id) : undefined;
              const timing = showMeasured ? timingIndex.get(o.id) : undefined;
              return (
                <ExpandableRow
                  key={o.id}
                  name={nameOf(o)}
                  expandable
                  expanded={expandedObj === o.id}
                  onToggle={() => setExpandedObj(expandedObj === o.id ? null : o.id)}
                  colSpan={colSpan}
                  detail={
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <PropertyList
                          columns={1}
                          items={[
                            { id: "id", label: t("objects.objectId"), value: o.id, mono: true },
                          ]}
                        />
                        {o.titleSource !== "own" && (
                          <p className="text-[12px] leading-relaxed text-dim">
                            {t(`objects.titleSource.${o.titleSource}`, { title: o.title })}
                          </p>
                        )}
                      </div>
                      {coverage && <QlikObjectDetail coverage={coverage} />}
                      {timing && <MeasuredObjectDetail timing={timing} />}
                      {o.expressions.length > 0 && (
                        <div>
                          <p className="mb-2 text-xs font-medium text-dim">
                            {t("objects.expressions", { count: o.expressions.length })}
                          </p>
                          <div className="space-y-2">
                            {o.expressions.map((e, i) => (
                              <div key={i} className="flex items-start gap-3">
                                <RiskBadge level={e.riskLevel} score={e.riskScore} />
                                <code className="min-w-0 flex-1 font-mono text-xs break-words whitespace-pre-wrap text-dim">
                                  {e.expression}
                                </code>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  }
                >
                  <TableCell wrap className="font-medium">
                    {!o.title ? (
                      <span className="text-faint italic">{nameOf(o)}</span>
                    ) : o.titleSource === "own" ? (
                      o.title
                    ) : (
                      <HintedCell
                        label={o.title}
                        hint={t(`objects.titleSource.${o.titleSource}`, { title: o.title })}
                        className=""
                      />
                    )}
                  </TableCell>
                  <TableCell wrap className="font-mono text-dim">
                    {o.type}
                  </TableCell>
                  <TableCell wrap className="text-dim">
                    {sheetTitle === undefined ? (
                      <span className="text-faint">—</span>
                    ) : sheetTitle === null ? (
                      <span className="inline-flex items-center gap-1 text-faint">
                        <FolderX className="size-3 shrink-0" strokeWidth={1.8} aria-hidden />
                        {t("usageRefs.noSheet")}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1">
                        <Folder
                          className="size-3 shrink-0 text-faint"
                          strokeWidth={1.8}
                          aria-hidden
                        />
                        {sheetTitle}
                      </span>
                    )}
                  </TableCell>
                  <TableCell wrap>
                    <RiskFactorBadges factors={o.riskFactors} />
                  </TableCell>
                  <TableCell align="end">
                    <RiskBadge level={o.riskLevel} score={o.riskScore} />
                  </TableCell>
                  {showQlik && (
                    <TableCell align="end">
                      <QlikTimeCell coverage={coverage} />
                    </TableCell>
                  )}
                  {showMeasured && (
                    <TableCell align="end">
                      <MeasuredTimeCell timing={timing} contextStatus={selectedContext?.status} />
                      {timing?.reusedFromObjectId && (
                        <div>
                          <HintedCell
                            label={t("objectTimings.reused")}
                            hint={t("objectTimings.reusedHint", {
                              objectId: timing.reusedFromObjectId,
                            })}
                          />
                        </div>
                      )}
                    </TableCell>
                  )}
                </ExpandableRow>
              );
            })}
          </TableBody>
        </Table>
      </AnalysisTableCard>
    </div>
  );
}
